const APP_DOMAINS = ['ontreams.com', 'acceptance-ontreams.com', 'testing-ontreams.com'] as const;

/**
 * The apex the app is served from, used to bounce an OAuth callback on to the
 * target workspace. Undefined for any host that is not a known Treams domain,
 * including localhost, where the callback already landed on the right origin
 * and no bounce is needed. Deliberately an allowlist and not label trimming:
 * the redirect carries an authorization code, so an unrecognised host must
 * fail closed.
 * @param hostname the host serving the page, i.e. `window.location.hostname`
 */
export function appDomain(hostname: string): string | undefined {
	return APP_DOMAINS.find((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

const SUBDOMAIN_LABEL = /^[a-z0-9-]+$/i;

/**
 * The authentication page of the workspace an OAuth `state` names, on the
 * given app domain. Uses the state's `alias`, falling back to its
 * `workspaceID`. The state travels through the browser, so the subdomain must
 * be a single DNS label; anything else yields undefined and must not be
 * redirected to.
 * @param state the raw `state` parameter of the OAuth callback
 * @param domain the apex returned by `appDomain()`
 * @param suffix the callback's hash or query string, forwarded as is
 */
export function workspaceAuthenticationUrl(
	state: string,
	domain: string,
	suffix: string,
): string | undefined {
	let parsed: { alias?: unknown; workspaceID?: unknown } | null;

	try {
		parsed = JSON.parse(state);
	} catch {
		return undefined;
	}

	const subdomain = parsed?.alias ?? parsed?.workspaceID;

	if (typeof subdomain !== 'string' || !SUBDOMAIN_LABEL.test(subdomain)) {
		return undefined;
	}

	return `https://${subdomain}.${domain}/authentication${suffix}`;
}
